import React, { useState, useEffect, useMemo, useCallback } from 'react';
import HeaderLogged from './components/header_logged';
import api from './api/client';
import MonthlyPlanTable from './components/PlanToProduceMonthly';
import WeeklyPlanTable from './components/PlanToProduceWeekly';
import PlanToolbar from './components/PlanToolbar';
import { getCurrentMiamiWeekRange, toMiamiDateString } from './utils/dateUtils';
import './PlanToProducePage.css';

const STORAGE_KEY = 'ptp_view_settings';

const PlanToProducePage = () => {
  const [activeTab, setActiveTab] = useState('monthly');
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [niches, setNiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [planRefreshKey, setPlanRefreshKey] = useState(0);

  // Persistent filter/view settings
  const loadSettings = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  };

  const savedSettings = loadSettings();

  const [searchQuery, setSearchQuery] = useState(savedSettings.searchQuery || '');
  const [selectedBrands, setSelectedBrands] = useState(savedSettings.selectedBrands || []);
  const [selectedNiches, setSelectedNiches] = useState(savedSettings.selectedNiches || []);
  const [weeklyDateRange, setWeeklyDateRange] = useState(() => {
    const saved = savedSettings.weeklyDateRange;
    if (!saved) return getCurrentMiamiWeekRange();
    // Если сохранённый диапазон уже в прошлом — показываем текущую неделю
    const today = toMiamiDateString(new Date());
    if (saved.end < today) return getCurrentMiamiWeekRange();
    return saved;
  });

  // Persist settings
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        searchQuery,
        selectedBrands,
        selectedNiches,
        weeklyDateRange,
      }));
    } catch {}
  }, [searchQuery, selectedBrands, selectedNiches, weeklyDateRange]);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [prodRes, brandRes, nicheRes] = await Promise.all([
          api.get('/plantoproduce/products'),
          api.get('/plantoproduce/brands'),
          api.get('/plantoproduce/niches'),
        ]);
        const [prodData, brandData, nicheData] = [
          prodRes.data,
          brandRes.data,
          nicheRes.data,
        ];
        setProducts(prodData);
        setBrands(brandData);
        setNiches(nicheData);
      } catch (err) {
        console.error('Error loading products/meta:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchMeta();
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const q = searchQuery.toLowerCase();
      const matchSearch = !q ||
        p.name?.toLowerCase().includes(q) ||
        p.SKU?.toLowerCase().includes(q) ||
        p.ASIN?.toLowerCase().includes(q);
      const matchBrand = selectedBrands.length === 0 || selectedBrands.includes(String(p.brandID));
      const matchNiche = selectedNiches.length === 0 || selectedNiches.includes(String(p.niche_category_id));
      return matchSearch && matchBrand && matchNiche;
    });
  }, [products, searchQuery, selectedBrands, selectedNiches]);

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedBrands([]);
    setSelectedNiches([]);
  }, []);

  const hasActiveFilters = searchQuery || selectedBrands.length > 0 || selectedNiches.length > 0;

  const handleRefreshFromSheets = async () => {
    setRefreshing(true);
    const errors = [];
    let monthlyUpdated = 0;

    try {
      // 1. Sync monthly plan (plan_to_produce table)
      const monthlyRes = await api.get('/plantoproduce/refresh-from-sheets');
      const monthlyResult = monthlyRes.data;
      if (!monthlyResult.success) {
        errors.push(`Monthly plan: ${monthlyResult.error || 'Unknown error'}`);
      } else {
        monthlyUpdated = monthlyResult.updated;
        window._lastSyncYears = monthlyResult.yearSummary || {};
        window._lastSyncPlanColumns = monthlyResult.planColumns || [];
        console.log('[sync] planColumns:', monthlyResult.planColumns);
        console.log('[sync] yearSummary:', monthlyResult.yearSummary);
      }
    } catch (err) {
      console.error('Monthly refresh error:', err);
      errors.push(err.response ? `Monthly plan: ${err.response.data?.error || 'Unknown error'}` : `Monthly plan: network error`);
    }

    try {
      // 2. Sync daily plan (masterbags_dailyplan + smallbags_dailyplan + seeds_dailyplan)
      await api.post('/api/update_daily_planning');
    } catch (err) {
      console.error('Daily refresh error:', err);
      errors.push(err.response ? `Daily plan: ${err.response.data?.error || 'Unknown error'}` : `Daily plan: network error`);
    }

    setRefreshing(false);

    if (errors.length > 0) {
      alert(`Sync completed with errors:\n• ${errors.join('\n• ')}`);
    } else {
      // Show year breakdown so we can verify 2027 data was picked up
      let msg = `Successfully synced!\nMonthly plan: ${monthlyUpdated} entries updated.`;
      if (window._lastSyncYears) {
        const years = Object.entries(window._lastSyncYears)
          .map(([y, n]) => `  ${y}: ${n} records`).join('\n');
        msg += `\n\nRecords by year:\n${years}`;
      }
      alert(msg);
    }
    // Re-fetch plan data so the table reflects newly synced values
    setPlanRefreshKey(k => k + 1);
  };

  return (
    <div className="ptp-root">
      <HeaderLogged />
      <div className="ptp-container">

        {/* Page header */}
        <div className="ptp-page-header">
          <div>
            <h2 className="ptp-title">Plan to Produce</h2>
            <p className="ptp-subtitle">Manage monthly and daily production planning</p>
          </div>
          <button
            className={`ptp-refresh-btn${refreshing ? ' loading' : ''}`}
            onClick={handleRefreshFromSheets}
            disabled={refreshing}
          >
            <i className={`fas fa-sync-alt${refreshing ? ' spin' : ''}`}></i>
            {refreshing ? 'Syncing...' : 'Sync from Sheets'}
          </button>
        </div>

        {/* Tabs */}
        <div className="ptp-tabs">
          <button
            className={`ptp-tab${activeTab === 'monthly' ? ' active' : ''}`}
            onClick={() => setActiveTab('monthly')}
          >
            <i className="fas fa-calendar-alt"></i>
            Monthly Plan
          </button>
          <button
            className={`ptp-tab${activeTab === 'weekly' ? ' active' : ''}`}
            onClick={() => setActiveTab('weekly')}
          >
            <i className="fas fa-calendar-week"></i>
            Daily Plan
          </button>
        </div>

        {/* Toolbar */}
        <PlanToolbar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedBrands={selectedBrands}
          setSelectedBrands={setSelectedBrands}
          selectedNiches={selectedNiches}
          setSelectedNiches={setSelectedNiches}
          brands={brands}
          niches={niches}
          filteredCount={filteredProducts.length}
          totalCount={products.length}
          hasActiveFilters={hasActiveFilters}
          clearFilters={clearFilters}
          activeTab={activeTab}
          weeklyDateRange={weeklyDateRange}
          setWeeklyDateRange={setWeeklyDateRange}
        />

        {/* Content */}
        {loading ? (
          <div className="ptp-loading">
            <i className="fas fa-circle-notch spin"></i>
            <span>Loading products…</span>
          </div>
        ) : (
          <>
            {activeTab === 'monthly' && (
              <MonthlyPlanTable products={filteredProducts} refreshKey={planRefreshKey} />
            )}
            {activeTab === 'weekly' && (
              <WeeklyPlanTable products={filteredProducts} dateRange={weeklyDateRange} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PlanToProducePage;